/**
 * Kayıt Geçmişi Modal'ı — BOSS personel giriş/çıkış kayıtlarını yönetir.
 * Filtreleme: personel + tarih aralığı. Her kayıt: saati düzenle + sil.
 * Üstte: manuel kayıt ekle.
 *
 * `acilPersonelId` verilirse o personel seçili açılır ve otomatik listelenir
 * (Giriş/Çıkış sayfasında karta tıklayınca).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ArrowDownToLine, ArrowUpFromLine, History, Filter, Loader2,
  Pencil, Trash2, Plus, Save, X, Clock, Coffee,
} from 'lucide-react'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import { Modal } from '@/components/ui/Modal'
import { DatePicker } from '@/components/ui/DatePicker'
import { tumPersonelleriGetir } from '@/lib/services/personel-service'
import { tumSubeleriGetir } from '@/lib/services/sube-service'
import {
  kayitZamaniGuncelle, kayitSil, type Kayit,
} from '@/lib/services/kayit-service'
import {
  araliktakiMolalar, molaZamanGuncelle, molaSil,
  MOLA_HAKKI_DAKIKA, type Mola,
} from '@/lib/services/mola-service'
import { ManuelKayitEkleModal } from './ManuelKayitEkleModal'
import type { KullaniciPersonel, Sube } from '@/types'

interface Props {
  acik: boolean
  onKapat: () => void
  /** Verilirse bu personel seçili açılır + otomatik listelenir */
  acilPersonelId?: string
}

function gunOnce(n: number): string {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return isoTarih(d)
}
function bugun(): string {
  return isoTarih(new Date())
}
function isoTarih(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function saatStr(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function yontemEtiket(y: string): string | null {
  if (y === 'qr') return null
  if (y === 'manuelBoss') return 'BOSS manuel'
  if (y === 'manuelSorumlu') return 'Sorumlu manuel'
  if (y === 'otomatik') return 'Otomatik'
  return y
}

export function KayitGecmisiModal({ acik, onKapat, acilPersonelId }: Props) {
  const [personeller, setPersoneller] = useState<KullaniciPersonel[]>([])
  const [subeler, setSubeler] = useState<Sube[]>([])

  const [personelId, setPersonelId] = useState('')
  const [baslangic, setBaslangic] = useState(gunOnce(7))
  const [bitis, setBitis] = useState(bugun())

  const [kayitlar, setKayitlar] = useState<Kayit[]>([])
  const [molalar, setMolalar] = useState<Mola[]>([])
  const [yukleniyor, setYukleniyor] = useState(false)
  const [hata, setHata] = useState<string | null>(null)
  const [aranYap, setAranYap] = useState(false)

  // Düzenleme state
  const [duzenId, setDuzenId] = useState<string | null>(null)
  const [duzenTarih, setDuzenTarih] = useState('')
  const [duzenSaat, setDuzenSaat] = useState('')
  const [islemId, setIslemId] = useState<string | null>(null)

  // Mola düzenleme state
  const [molaDuzenId, setMolaDuzenId] = useState<string | null>(null)
  const [mdBasTarih, setMdBasTarih] = useState('')
  const [mdBasSaat, setMdBasSaat] = useState('')
  const [mdBitTarih, setMdBitTarih] = useState('')
  const [mdBitSaat, setMdBitSaat] = useState('')

  // Manuel ekle modal
  const [manuelAcik, setManuelAcik] = useState(false)

  const ara = useCallback(async (pid: string) => {
    if (!pid) {
      setHata('Personel seç')
      return
    }
    setYukleniyor(true)
    setHata(null)
    setAranYap(true)
    try {
      const db = getFirestoreDb()
      const q = query(collection(db, 'kayitlar'), where('personelId', '==', pid))
      const snap = await getDocs(q)
      const tum = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Kayit)
      const [by, bm, bd] = baslangic.split('-').map(Number)
      const [ey, em, ed] = bitis.split('-').map(Number)
      const basMs = new Date(by!, bm! - 1, bd!, 0, 0, 0, 0).getTime()
      const bitMs = new Date(ey!, em! - 1, ed!, 23, 59, 59, 999).getTime()
      const filtreli = tum
        .filter((k) => {
          const t = k.zaman.toMillis()
          return t >= basMs && t <= bitMs
        })
        .sort((a, b) => b.zaman.toMillis() - a.zaman.toMillis())
      setKayitlar(filtreli)
      const mlist = await araliktakiMolalar(pid, baslangic, bitis)
      setMolalar(mlist)
    } catch (err) {
      console.error(err)
      setHata((err as Error).message)
    } finally {
      setYukleniyor(false)
    }
  }, [baslangic, bitis])

  // Açılışta personel + şube yükle, acilPersonelId varsa otomatik listele
  useEffect(() => {
    if (!acik) return
    setManuelAcik(false)
    setDuzenId(null)
    setMolaDuzenId(null)
    Promise.all([tumPersonelleriGetir(), tumSubeleriGetir()])
      .then(([p, s]) => {
        setPersoneller(p.filter((x) => x.aktif))
        setSubeler(s.filter((x) => x.aktif))
        if (acilPersonelId) {
          setPersonelId(acilPersonelId)
          setBaslangic(gunOnce(7))
          setBitis(bugun())
        }
      })
      .catch((err) => {
        console.error(err)
        setHata('Veri yüklenemedi')
      })
  }, [acik, acilPersonelId])

  // acilPersonelId değişince otomatik ara
  useEffect(() => {
    if (acik && acilPersonelId) {
      ara(acilPersonelId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acik, acilPersonelId])

  const subeHaritasi = useMemo(() => {
    const m = new Map<string, Sube>()
    subeler.forEach((s) => m.set(s.id, s))
    return m
  }, [subeler])

  function duzenleBaslat(k: Kayit) {
    const d = k.zaman.toDate()
    setDuzenId(k.id)
    setDuzenTarih(isoTarih(d))
    setDuzenSaat(saatStr(d))
  }

  async function duzenleKaydet(k: Kayit) {
    setIslemId(k.id)
    setHata(null)
    try {
      const [y, m, d] = duzenTarih.split('-').map(Number)
      const [h, mi] = duzenSaat.split(':').map(Number)
      const yeni = new Date(y!, m! - 1, d!, h!, mi!, 0, 0)
      await kayitZamaniGuncelle(k.id, yeni)
      setDuzenId(null)
      await ara(personelId)
    } catch (err) {
      setHata((err as Error).message)
    } finally {
      setIslemId(null)
    }
  }

  async function sil(k: Kayit) {
    const z = k.zaman.toDate().toLocaleString('tr-TR', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    })
    if (!window.confirm(`${k.tip === 'giris' ? 'Giriş' : 'Çıkış'} kaydı (${z}) silinsin mi? Bu işlem geri alınamaz.`)) {
      return
    }
    setIslemId(k.id)
    setHata(null)
    try {
      await kayitSil(k.id)
      await ara(personelId)
    } catch (err) {
      setHata((err as Error).message)
    } finally {
      setIslemId(null)
    }
  }

  function molaSureDk(m: Mola): number {
    const bit = m.bitis ? m.bitis.toMillis() : Date.now()
    return Math.max(0, Math.floor((bit - m.baslangic.toMillis()) / 60000))
  }

  function molaDuzenleBaslat(m: Mola) {
    const b = m.baslangic.toDate()
    setMolaDuzenId(m.id)
    setMdBasTarih(isoTarih(b))
    setMdBasSaat(saatStr(b))
    const bit = m.bitis ? m.bitis.toDate() : new Date(b.getTime() + 30 * 60000)
    setMdBitTarih(isoTarih(bit))
    setMdBitSaat(saatStr(bit))
  }

  async function molaDuzenleKaydet(m: Mola) {
    setIslemId(m.id)
    setHata(null)
    try {
      const [by, bm, bd] = mdBasTarih.split('-').map(Number)
      const [bh, bmin] = mdBasSaat.split(':').map(Number)
      const bas = new Date(by!, bm! - 1, bd!, bh!, bmin!, 0, 0)
      const [ey, em, ed] = mdBitTarih.split('-').map(Number)
      const [eh, emin] = mdBitSaat.split(':').map(Number)
      const bit = new Date(ey!, em! - 1, ed!, eh!, emin!, 0, 0)
      await molaZamanGuncelle(m.id, bas, bit)
      setMolaDuzenId(null)
      await ara(personelId)
    } catch (err) {
      setHata((err as Error).message)
    } finally {
      setIslemId(null)
    }
  }

  async function molaSilHandler(m: Mola) {
    if (!window.confirm(`${molaSureDk(m)} dk'lık mola silinsin mi? Mola aşımından kaynaklanan kesinti varsa geri alınır. Bu işlem geri alınamaz.`)) {
      return
    }
    setIslemId(m.id)
    setHata(null)
    try {
      await molaSil(m.id)
      await ara(personelId)
    } catch (err) {
      setHata((err as Error).message)
    } finally {
      setIslemId(null)
    }
  }

  return (
    <Modal acik={acik} onKapat={onKapat} baslik="Kayıt Geçmişi" boyut="lg">
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Personel
            </label>
            <select
              value={personelId}
              onChange={(e) => setPersonelId(e.target.value)}
              disabled={!!acilPersonelId}
              className="input-base disabled:bg-gray-100 disabled:text-gray-700 disabled:cursor-not-allowed"
            >
              <option value="">— Seç —</option>
              {personeller.map((p) => (
                <option key={p.id} value={p.id}>{p.isim}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Başlangıç
            </label>
            <DatePicker value={baslangic} onChange={setBaslangic} max={bitis} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Bitiş
            </label>
            <DatePicker value={bitis} onChange={setBitis} min={baslangic} />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => ara(personelId)}
              disabled={yukleniyor || !personelId}
              className="btn-primary w-full inline-flex items-center justify-center gap-2"
            >
              {yukleniyor ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Filter className="w-4 h-4" />
              )}
              Listele
            </button>
          </div>
        </div>

        {/* Manuel ekle butonu — personel seçiliyken */}
        {personelId && (
          <button
            onClick={() => setManuelAcik(true)}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-brand-300 text-brand-700 hover:bg-brand-50 rounded-xl text-sm font-semibold transition-colors"
          >
            <Plus className="w-4 h-4" />
            Manuel Kayıt Ekle
          </button>
        )}

        {hata && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-xl p-3">
            {hata}
          </div>
        )}

        {aranYap && !yukleniyor && (
          <div className="border-t border-gray-100 pt-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-gray-900 inline-flex items-center gap-2">
                <History className="w-4 h-4 text-gray-600" />
                Sonuç: {kayitlar.length} kayıt
              </h3>
            </div>

            {kayitlar.length === 0 ? (
              <div className="bg-gray-50 rounded-xl p-6 text-center text-gray-500 text-sm">
                Bu aralıkta kayıt yok.
              </div>
            ) : (
              <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                {kayitlar.map((k) => {
                  const sube = subeHaritasi.get(k.subeId)
                  const z = k.zaman.toDate()
                  const etiket = yontemEtiket(k.yontem)
                  const duzenleniyor = duzenId === k.id
                  const islemde = islemId === k.id

                  if (duzenleniyor) {
                    return (
                      <div key={k.id} className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5 space-y-2">
                        <p className="text-xs font-semibold text-gray-700">
                          {k.tip === 'giris' ? 'Giriş' : 'Çıkış'} saatini düzenle
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          <DatePicker value={duzenTarih} onChange={setDuzenTarih} max={bugun()} />
                          <div className="relative">
                            <input
                              type="time"
                              value={duzenSaat}
                              onChange={(e) => setDuzenSaat(e.target.value)}
                              className="input-base"
                            />
                            <Clock className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setDuzenId(null)}
                            disabled={islemde}
                            className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg disabled:opacity-50"
                          >
                            <X className="w-3.5 h-3.5" /> İptal
                          </button>
                          <button
                            onClick={() => duzenleKaydet(k)}
                            disabled={islemde}
                            className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50"
                          >
                            {islemde ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                            Kaydet
                          </button>
                        </div>
                      </div>
                    )
                  }

                  return (
                    <div
                      key={k.id}
                      className="flex items-center gap-3 bg-gray-50 hover:bg-gray-100 rounded-lg px-3 py-2 transition-colors"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        k.tip === 'giris' ? 'bg-green-100' : 'bg-orange-100'
                      }`}>
                        {k.tip === 'giris' ? (
                          <ArrowDownToLine className="w-4 h-4 text-green-700" />
                        ) : (
                          <ArrowUpFromLine className="w-4 h-4 text-orange-700" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {k.tip === 'giris' ? 'Giriş' : 'Çıkış'}
                          {etiket && (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
                              {etiket}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">
                          {sube?.isim ?? '?'}
                          {k.notlar && <span className="ml-2 italic">— {k.notlar}</span>}
                        </p>
                      </div>
                      <div className="text-xs text-gray-700 text-right flex-shrink-0 tabular-nums">
                        <div className="font-semibold">
                          {z.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                        </div>
                        <div className="text-gray-500">
                          {z.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => duzenleBaslat(k)}
                          disabled={islemde}
                          title="Saati düzenle"
                          className="p-1.5 text-gray-500 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors disabled:opacity-50"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => sil(k)}
                          disabled={islemde}
                          title="Sil"
                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {islemde ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* MOLALAR */}
        {aranYap && !yukleniyor && personelId && (
          <div className="border-t border-gray-100 pt-3">
            <h3 className="font-bold text-gray-900 inline-flex items-center gap-2 mb-2">
              <Coffee className="w-4 h-4 text-gray-600" />
              Molalar: {molalar.length}
            </h3>

            {molalar.length === 0 ? (
              <div className="bg-gray-50 rounded-xl p-4 text-center text-gray-500 text-sm">
                Bu aralıkta mola yok.
              </div>
            ) : (
              <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                {molalar.map((m) => {
                  const duzenleniyor = molaDuzenId === m.id
                  const islemde = islemId === m.id
                  const sure = molaSureDk(m)
                  const acik = !m.bitis
                  const asim = !acik && sure > MOLA_HAKKI_DAKIKA
                  const b = m.baslangic.toDate()

                  if (duzenleniyor) {
                    return (
                      <div key={m.id} className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5 space-y-2">
                        <p className="text-xs font-semibold text-gray-700">Mola saatlerini düzenle</p>
                        <div>
                          <p className="text-[11px] text-gray-500 mb-1">Başlangıç</p>
                          <div className="grid grid-cols-2 gap-2">
                            <DatePicker value={mdBasTarih} onChange={setMdBasTarih} max={bugun()} />
                            <input
                              type="time"
                              value={mdBasSaat}
                              onChange={(e) => setMdBasSaat(e.target.value)}
                              className="input-base"
                            />
                          </div>
                        </div>
                        <div>
                          <p className="text-[11px] text-gray-500 mb-1">Bitiş</p>
                          <div className="grid grid-cols-2 gap-2">
                            <DatePicker value={mdBitTarih} onChange={setMdBitTarih} max={bugun()} />
                            <input
                              type="time"
                              value={mdBitSaat}
                              onChange={(e) => setMdBitSaat(e.target.value)}
                              className="input-base"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setMolaDuzenId(null)}
                            disabled={islemde}
                            className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg disabled:opacity-50"
                          >
                            <X className="w-3.5 h-3.5" /> İptal
                          </button>
                          <button
                            onClick={() => molaDuzenleKaydet(m)}
                            disabled={islemde}
                            className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50"
                          >
                            {islemde ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                            Kaydet
                          </button>
                        </div>
                      </div>
                    )
                  }

                  return (
                    <div
                      key={m.id}
                      className={`flex items-center gap-3 rounded-lg px-3 py-2 transition-colors ${
                        acik ? 'bg-red-50 hover:bg-red-100' : asim ? 'bg-amber-50 hover:bg-amber-100' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-orange-100">
                        <Coffee className="w-4 h-4 text-orange-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {sure} dk
                          {acik && (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded">
                              Açık (bitmedi)
                            </span>
                          )}
                          {asim && (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
                              {sure - MOLA_HAKKI_DAKIKA} dk aşım
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500 tabular-nums">
                          {b.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                          {' · '}
                          {b.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                          {' → '}
                          {m.bitis
                            ? m.bitis.toDate().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
                            : '—'}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => molaDuzenleBaslat(m)}
                          disabled={islemde}
                          title="Saatleri düzenle"
                          className="p-1.5 text-gray-500 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors disabled:opacity-50"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => molaSilHandler(m)}
                          disabled={islemde}
                          title="Sil"
                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {islemde ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        <div className="flex pt-1">
          <button onClick={onKapat} className="btn-secondary w-full">
            Kapat
          </button>
        </div>
      </div>

      <ManuelKayitEkleModal
        acik={manuelAcik}
        onKapat={() => setManuelAcik(false)}
        sabitPersonelId={personelId || undefined}
        onKaydedildi={() => {
          setManuelAcik(false)
          if (personelId) ara(personelId)
        }}
      />
    </Modal>
  )
}
